// Server state via TanStack Query. Everything about one calendar lives under ['feed', id, …], so a
// single invalidation refreshes the header, calendar, layers, share info and history together.

import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type { Feed, FeedDetail, LayersData, Preview, ShareInfo, User } from './types';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
    },
  },
});

export const keys = {
  me: ['me'] as const,
  feeds: ['feeds'] as const,
  feed: (id: string) => ['feed', id] as const,
};

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: async () => (await api<{ user: User | null }>('/me')).user, staleTime: Infinity });
}

export function useFeeds() {
  return useQuery({ queryKey: keys.feeds, queryFn: async () => (await api<{ feeds: Feed[] }>('/feeds')).feeds });
}

export function useFeed(id: string) {
  return useQuery({ queryKey: keys.feed(id), queryFn: () => api<FeedDetail>(`/feeds/${id}`) });
}

export function usePreview(id: string, from: string | null, to: string | null) {
  return useQuery({
    queryKey: [...keys.feed(id), 'preview', from, to],
    queryFn: () => api<Preview>(`/feeds/${id}/preview?from=${from}&to=${to}`),
    enabled: !!from && !!to,
    placeholderData: (prev) => prev,
  });
}

export function useLayers(id: string, enabled = true) {
  return useQuery({ queryKey: [...keys.feed(id), 'layers'], queryFn: () => api<LayersData>(`/feeds/${id}/layers`), enabled });
}

export function useShare(id: string, enabled = true) {
  return useQuery({ queryKey: [...keys.feed(id), 'share'], queryFn: () => api<ShareInfo>(`/feeds/${id}/share`), enabled });
}

/** Refresh everything about a calendar (and the sidebar list). */
export function useRefreshFeed() {
  const qc = useQueryClient();
  return (id: string) => Promise.all([qc.invalidateQueries({ queryKey: keys.feed(id) }), qc.invalidateQueries({ queryKey: keys.feeds })]);
}
